import { useState, useEffect, useCallback } from 'react';
import { supabase, useAuth } from '@mondivago/supabase';
import { uploadOptimizedImage } from '@/shared/lib/storage';
import type { TravelerProfile } from '@mondivago/types';

export function useTravelerProfile() {
  const { user } = useAuth();
  const [profile, setProfile] = useState<TravelerProfile | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchProfile = useCallback(async () => {
    if (!user) {
      setLoading(false);
      return;
    }

    setLoading(true);
    const { data, error: fetchError } = await supabase
      .from('travelers')
      .select('*')
      .eq('user_id', user.id)
      .maybeSingle();

    if (fetchError) {
      setError('An unexpected error occurred. Please try again.');
    } else {
      setProfile(data);
    }
    setLoading(false);
  }, [user]);

  useEffect(() => {
    void Promise.resolve().then(() => {
      void fetchProfile();
    });
  }, [fetchProfile]);

  const updateProfile = async (updates: Partial<TravelerProfile>) => {
    if (!user) return { error: 'Not authenticated' };

    const { error: updateError } = await supabase.from('travelers').update(updates).eq('user_id', user.id);

    if (!updateError) {
      setProfile((prev) => (prev ? { ...prev, ...updates } : null));
      return { success: true };
    }

    return { error: 'An unexpected error occurred. Please try again.' };
  };

  const uploadPhoto = async (file: File) => {
    if (!user) return { error: 'Not authenticated', url: undefined };

    try {
      const publicUrl = await uploadOptimizedImage({
        file,
        bucket: 'users',
        folder: `travelers/${user.id}`,
        prefix: 'avatar',
      });
      const result = await updateProfile({ photo_url: publicUrl });
      return { ...result, url: publicUrl };
    } catch (err: unknown) {
      return { error: 'Upload failed. Please try again.', url: undefined };
    }
  };

  const deleteAccount = async () => {
    if (!user) return { error: 'Not authenticated' };

    const { error: deleteError } = await supabase.from('travelers').delete().eq('user_id', user.id);

    if (deleteError) {
      return { error: 'An unexpected error occurred. Please try again.' };
    }

    await supabase.auth.signOut();
    setProfile(null);
    return { success: true };
  };

  const updatePassword = async (password: string) => {
    const { error } = await supabase.auth.updateUser({ password });
    if (error) return { error: 'An unexpected error occurred. Please try again.' };
    return { success: true };
  };

  return {
    profile,
    loading,
    error,
    updateProfile,
    uploadPhoto,
    deleteAccount,
    updatePassword,
    refresh: fetchProfile,
  };
}
